import { useState, useEffect, useRef, useCallback } from 'react';
import { getAudienceSocket } from '../hooks/useAudienceState';
import NicknameBar from '../components/NicknameBar';
import { getSpriteById } from '../../data/mole-sprites';
import { useCountdown } from '../hooks/useCountdown';

interface MoleChallengeScreenProps {
  challengeId: string;
  config: {
    targetScore: number;
    teleportIntervalMs?: number;
    moleSprite?: string;
  };
  endsAt: number;
  serverTime: number;
  initialScore: number;
  targetScore: number;
}

interface Explosion {
  id: number;
  x: number;
  y: number;
}

let explosionIdCounter = 0;

function MoleChallengeScreen({
  challengeId,
  config,
  endsAt,
  serverTime,
  initialScore,
  targetScore,
}: MoleChallengeScreenProps) {
  const remainingSeconds = useCountdown(endsAt, serverTime);
  const [score, setScore] = useState(initialScore);
  const [molePosition, setMolePosition] = useState({ x: 50, y: 50 });
  const [explosions, setExplosions] = useState<Explosion[]>([]);
  const teleportTimerRef = useRef<ReturnType<typeof setInterval>>();
  const containerRef = useRef<HTMLDivElement>(null);

  const teleportInterval = config.teleportIntervalMs || 1200;
  const progressPercent = Math.min(100, Math.round((score / targetScore) * 100));

  // Teleporta la talpa in posizione random
  const teleportMole = useCallback(() => {
    const x = 10 + Math.random() * 80;
    const y = 15 + Math.random() * 70;
    setMolePosition({ x, y });
  }, []);

  // Auto-teleport
  useEffect(() => {
    teleportMole();
    teleportTimerRef.current = setInterval(teleportMole, teleportInterval);
    return () => {
      if (teleportTimerRef.current) clearInterval(teleportTimerRef.current);
    };
  }, [teleportMole, teleportInterval]);

  // Ascolta tick dal server per aggiornamento score
  useEffect(() => {
    const socket = getAudienceSocket();
    if (!socket) return;

    const handleTick = (data: { challengeId: string; score: number }) => {
      if (data.challengeId === challengeId) {
        setScore(data.score);
      }
    };

    socket.on('challenge:tick', handleTick);
    return () => { socket.off('challenge:tick', handleTick); };
  }, [challengeId]);

  // Gestisci tap sulla talpa
  const handleMoleTap = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const socket = getAudienceSocket();
    if (!socket) return;

    socket.emit('challenge:tap', { challengeId, variant: 'mole' });

    // Esplosione nella posizione corrente della talpa
    const id = ++explosionIdCounter;
    setExplosions(prev => [...prev, { id, x: molePosition.x, y: molePosition.y }]);
    setTimeout(() => {
      setExplosions(prev => prev.filter(e => e.id !== id));
    }, 1000);

    // Teleporta subito
    teleportMole();

    // Reset timer auto-teleport
    if (teleportTimerRef.current) clearInterval(teleportTimerRef.current);
    teleportTimerRef.current = setInterval(teleportMole, teleportInterval);
  };

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col min-h-screen overflow-hidden select-none"
      style={{ touchAction: 'manipulation' }}
    >
      <NicknameBar />

      {/* Header: countdown + progress */}
      <div className="pt-12 px-4 pb-2 z-10">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-2xl font-heading font-bold ${remainingSeconds <= 5 ? 'text-team-red animate-pulse' : 'text-ink'}`}>
            {remainingSeconds}s
          </span>
          <span className="text-ink-light text-base font-heading">{score}/{targetScore}</span>
        </div>
        {/* Progress bar */}
        <div className="progress-parchment">
          <div
            className="h-full bg-gradient-to-r from-yellow-500 to-green-500 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Area di gioco */}
      <div className="flex-1 relative">
        {/* Esplosioni */}
        {explosions.map((exp) => (
          <div
            key={exp.id}
            className="absolute pointer-events-none"
            style={{
              left: `${exp.x}%`,
              top: `${exp.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {/* Anello che si espande */}
            <div
              className="absolute rounded-full"
              style={{
                width: 16,
                height: 16,
                left: -8,
                top: -8,
                border: '3px solid #ff6b6b',
                animation: 'explosionRing 0.4s ease-out forwards',
              }}
            />
            {/* Particelle che schizzano fuori */}
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (i / 8) * 360;
              return (
                <div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    width: 6,
                    height: 6,
                    left: -3,
                    top: -3,
                    background: i % 2 === 0 ? '#ffcc00' : '#ff6b6b',
                    animation: `explosionParticle 0.4s ease-out forwards`,
                    '--ex': `${Math.cos((angle * Math.PI) / 180) * 50}px`,
                    '--ey': `${Math.sin((angle * Math.PI) / 180) * 50}px`,
                  } as React.CSSProperties}
                />
              );
            })}
            {/* +1 che fluttua verso l'alto e sfuma */}
            <div
              className="absolute font-bold text-2xl text-yellow-300 whitespace-nowrap"
              style={{
                left: '50%',
                bottom: '100%',
                transform: 'translateX(-50%)',
                textShadow: '0 0 8px rgba(0,0,0,0.7)',
                animation: 'floatUp 1s ease-out forwards',
              }}
            >
              +1
            </div>
          </div>
        ))}

        {/* La talpa */}
        {(() => {
          const sprite = getSpriteById(config.moleSprite || 'default');
          return (
            <div
              className="absolute w-16 h-16 cursor-pointer"
              style={{
                left: `${molePosition.x}%`,
                top: `${molePosition.y}%`,
                transform: 'translate(-50%, -50%)',
                ...(sprite.id === 'default' ? {
                  boxShadow: `0 0 20px ${sprite.glowColor}, 0 0 40px ${sprite.glowColor.replace('0.6', '0.3')}`,
                  borderRadius: '50%',
                } : {}),
              }}
              onTouchStart={handleMoleTap}
              onMouseDown={handleMoleTap}
            >
              {sprite.render(64)}
            </div>
          );
        })()}
      </div>

      <style>{`
        @keyframes explosionRing {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(6); opacity: 0; }
        }
        @keyframes explosionParticle {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(var(--ex), var(--ey)) scale(0); opacity: 0; }
        }
        @keyframes floatUp {
          0% { transform: translateX(-50%) translateY(0); opacity: 1; }
          100% { transform: translateX(-50%) translateY(-60px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default MoleChallengeScreen;
